/**
 * Format phone/mobile for table display (e.g. 4565465556 → 456-546-5556).
 * Search/sort should use raw values; this is display-only.
 */
export function formatMobileDisplay(value) {
    if (value == null) return '';
    const raw = String(value).trim();
    if (!raw || raw.toUpperCase() === 'N/A') return raw;

    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;

    let local = digits;
    if (digits.length === 12 && digits.startsWith('91')) {
        local = digits.slice(2);
    } else if (digits.length === 11 && digits.startsWith('0')) {
        local = digits.slice(1);
    }

    if (local.length === 10) {
        return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
    }
    if (local.length > 6) {
        return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
    }
    if (local.length > 3) {
        return `${local.slice(0, 3)}-${local.slice(3)}`;
    }
    return local;
}

export function formatMobileDisplayOrFallback(value, fallback = 'N/A') {
    const primary = value != null && String(value).trim() !== '' ? value : null;
    if (!primary) return fallback;
    const formatted = formatMobileDisplay(primary);
    return formatted || fallback;
}

/** Normalize entity status for admin ACTIVE/INACTIVE dropdown. */
export function normalizeAdminEntityStatus(status) {
    const u = String(status ?? '').trim().toUpperCase();
    if (u === 'I' || u === 'INACTIVE') return 'INACTIVE';
    if (u === 'A' || u === 'ACTIVE') return 'ACTIVE';
    return u || 'INACTIVE';
}

export function isInactiveEntityStatus(status) {
    return normalizeAdminEntityStatus(status) === 'INACTIVE';
}

export function pickMobileValue(...candidates) {
    for (const candidate of candidates) {
        if (candidate == null) continue;
        const trimmed = String(candidate).trim();
        if (trimmed && trimmed.toUpperCase() !== 'N/A') return candidate;
    }
    return null;
}

/** Resolve mobile from order / contact records with varying API field names. */
export function resolveOrderMobile(record) {
    if (!record) return null;
    return pickMobileValue(
        record.mobile,
        record.customerMobile,
        record.contactNumber,
        record.customerContact,
        record.Contact_Number,
        record.Delivery_Contact_Number
    );
}

export function resolveOrderAltMobile(record) {
    if (!record) return null;
    return pickMobileValue(record.altNumber, record.Alt_Number, record.altNo, record.Alt_No);
}

export function formatOrderMobileDisplay(record, fallback = 'N/A') {
    return formatMobileDisplayOrFallback(resolveOrderMobile(record), fallback);
}

export function formatOrderAltMobileDisplay(record, fallback = 'N/A') {
    return formatMobileDisplayOrFallback(resolveOrderAltMobile(record), fallback);
}

export const MOBILE_DIGITS_LENGTH = 10;

/** Strip non-digits and cap at 10 characters for mobile inputs. */
export function sanitizeMobileInput(value) {
    return String(value ?? '').replace(/\D/g, '').slice(0, MOBILE_DIGITS_LENGTH);
}

export function isValidMobileNumber(value, { required = true } = {}) {
    const digits = sanitizeMobileInput(value);
    if (!digits) return !required;
    return digits.length === MOBILE_DIGITS_LENGTH;
}

/** Returns an error message, or empty string when valid. */
export function validateMobileNumber(value, { required = true, label = 'Mobile number' } = {}) {
    const digits = sanitizeMobileInput(value);
    if (!digits) {
        return required ? `${label} is required.` : '';
    }
    if (digits.length !== MOBILE_DIGITS_LENGTH) {
        return `${label} must be exactly ${MOBILE_DIGITS_LENGTH} digits.`;
    }
    return '';
}

export const AADHAR_DIGITS_LENGTH = 12;

/** Strip non-digits and cap at 12 characters for Aadhar inputs. */
export function sanitizeAadharInput(value) {
    return String(value ?? '').replace(/\D/g, '').slice(0, AADHAR_DIGITS_LENGTH);
}

export function isValidAadharNumber(value, { required = true } = {}) {
    const digits = sanitizeAadharInput(value);
    if (!digits) return !required;
    return digits.length === AADHAR_DIGITS_LENGTH;
}

/** Returns an error message, or empty string when valid. */
export function validateAadharNumber(value, { required = true, label = 'Aadhar number' } = {}) {
    const digits = sanitizeAadharInput(value);
    if (!digits) {
        return required ? `${label} is required.` : '';
    }
    if (digits.length !== AADHAR_DIGITS_LENGTH) {
        return `${label} must be exactly ${AADHAR_DIGITS_LENGTH} digits.`;
    }
    return '';
}

/** Display Aadhar as XXXX XXXX XXXX. Search/sort/API should use raw digits. */
export function formatAadharDisplay(value) {
    const digits = sanitizeAadharInput(value);
    if (!digits) return '';
    const parts = [];
    for (let i = 0; i < digits.length; i += 4) {
        parts.push(digits.slice(i, i + 4));
    }
    return parts.join(' ');
}

export function formatAadharDisplayOrFallback(value, fallback = '—') {
    const formatted = formatAadharDisplay(value);
    return formatted || fallback;
}

export const FSSAI_DIGITS_LENGTH = 14;

/** Strip non-digits and cap at 14 characters for FSSAI license inputs. */
export function sanitizeFssaiInput(value) {
    return String(value ?? '').replace(/\D/g, '').slice(0, FSSAI_DIGITS_LENGTH);
}

export function isValidFssaiNumber(value, { required = true } = {}) {
    const digits = sanitizeFssaiInput(value);
    if (!digits) return !required;
    return digits.length === FSSAI_DIGITS_LENGTH;
}

/** Returns an error message, or empty string when valid. */
export function validateFssaiNumber(value, { required = true, label = 'FSSAI number' } = {}) {
    const digits = sanitizeFssaiInput(value);
    if (!digits) {
        return required ? `${label} is required.` : '';
    }
    if (digits.length !== FSSAI_DIGITS_LENGTH) {
        return `${label} must be exactly ${FSSAI_DIGITS_LENGTH} digits.`;
    }
    return '';
}

/** Display FSSAI as XXXX XXXX XXXX XX. Search/sort/API should use raw digits. */
export function formatFssaiDisplay(value) {
    const digits = sanitizeFssaiInput(value);
    if (!digits) return '';
    if (digits.length <= 4) return digits;
    if (digits.length <= 8) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    if (digits.length <= 12) return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12)}`;
}

export function formatFssaiDisplayOrFallback(value, fallback = '—') {
    const formatted = formatFssaiDisplay(value);
    return formatted || fallback;
}

/**
 * Parse API date strings (`YYYY-MM-DD HH:mm:ss` or ISO) into a Date.
 * @returns {Date|null}
 */
export function parseOrderDateTime(value) {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const raw = String(value).trim();
    if (!raw) return null;
    const parsed = new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) ? raw.replace(' ', 'T') : raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Display order created/updated as DD/MM/YYYY HH:mm */
export function formatOrderDateTime(value, fallback = '—') {
    const parsed = parseOrderDateTime(value);
    if (!parsed) {
        const raw = value != null ? String(value).trim() : '';
        return raw || fallback;
    }
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/** Resolve created/updated-by user id from order API fields. */
export function resolveOrderCreatedById(order) {
    if (!order || typeof order !== 'object') return null;
    const id =
        order.createdById ??
        order.created_by_id ??
        order.Created_By_Id ??
        order.Created_By ??
        null;
    if (id == null || id === '') return null;
    return id;
}

/**
 * Title-case a display label: "cash" → "Cash", "BANK-TRANSFER" → "Bank-Transfer".
 * Leaves empty / N/A as fallback.
 */
export function toDisplayLabel(value, fallback = '—') {
    if (value == null) return fallback;
    if (Array.isArray(value)) {
        const parts = value
            .map((part) => toDisplayLabel(part, ''))
            .filter(Boolean);
        return parts.length ? parts.join(', ') : fallback;
    }
    const raw = String(value).trim();
    if (!raw || ['n/a', 'null', 'undefined', '—', '-', 'none'].includes(raw.toLowerCase())) {
        return fallback;
    }
    return raw
        .split(/([,\s]+)/)
        .map((token) => {
            if (/^[,\s]+$/.test(token)) return token;
            return token
                .split(/([-_/])/)
                .map((chunk) => {
                    if (chunk === '-' || chunk === '_' || chunk === '/') return chunk === '_' ? '-' : chunk;
                    if (!chunk) return chunk;
                    return chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
                })
                .join('');
        })
        .join('');
}

/** Resolve payment method from order / nested payment (API field name variants). */
export function resolveOrderPaymentMethod(order) {
    if (!order || typeof order !== 'object') return null;
    const payment = order.payment ?? order.Payment ?? null;
    const raw =
        order.paymentMethod ??
        order.payment_method ??
        order.Payment_Method ??
        order.PaymentMethod ??
        payment?.paymentMethod ??
        payment?.payment_method ??
        payment?.Payment_Method ??
        payment?.PaymentMethod ??
        null;
    if (raw == null) return null;
    if (Array.isArray(raw)) {
        const parts = raw.map((m) => String(m ?? '').trim()).filter(Boolean);
        return parts.length ? parts : null;
    }
    const text = String(raw).trim();
    return text || null;
}

export function formatPaymentMethodDisplay(orderOrMethod, fallback = '—') {
    const method =
        orderOrMethod && typeof orderOrMethod === 'object' && !Array.isArray(orderOrMethod)
            ? resolveOrderPaymentMethod(orderOrMethod)
            : orderOrMethod;
    return toDisplayLabel(method, fallback);
}

/**
 * Prefer business display order number; fall back to internal order id / grocery id.
 * Use for POS labels, history, receipt bill num — keep orderId for API navigation.
 */
export function resolveDisplayOrderNo(order, fallback = null) {
    if (order == null || order === '') {
        return fallback;
    }
    if (typeof order !== 'object') {
        const text = String(order).trim();
        return text || fallback;
    }
    const candidates = [
        order.displayOrderNo,
        order.display_order_no,
        order.Display_Order_No,
        order.displayOrderRef,
        order.display_order_ref,
        order.billNo,
        order.bill_no,
        order.orderNumber,
        order.order_number,
        order.orderId,
        order.order_id,
        order.Order_Id,
        order.groceryOrderId,
        order.grocery_order_id,
        order.cateringOrderId,
        order.catering_order_id,
    ];
    for (const candidate of candidates) {
        if (candidate == null) continue;
        const text = String(candidate).trim();
        if (text) return text;
    }
    return fallback;
}

export function formatDisplayOrderNo(order, fallback = '—') {
    return resolveDisplayOrderNo(order, fallback) ?? fallback;
}

export function isCashPaymentMethod(orderOrMethod) {
    const method =
        orderOrMethod && typeof orderOrMethod === 'object' && !Array.isArray(orderOrMethod)
            ? resolveOrderPaymentMethod(orderOrMethod)
            : orderOrMethod;
    if (Array.isArray(method)) {
        return method.length > 0 && method.every((m) => String(m).trim().toUpperCase() === 'CASH');
    }
    return String(method || '').trim().toUpperCase() === 'CASH';
}

/** Signed-in user's first + last name for headers and account menus. */
export function formatUserFullName(user, fallback = 'User') {
    if (!user || typeof user !== 'object') return fallback;
    const first = String(user.firstName ?? user.first_name ?? user.First_Name ?? '').trim();
    const last = String(user.lastName ?? user.last_name ?? user.Last_Name ?? '').trim();
    if (first && last && !first.toLowerCase().includes(last.toLowerCase())) {
        return `${first} ${last}`;
    }
    if (first) return first;
    if (last) return last;
    const name = String(user.name ?? '').trim();
    if (name) return name;
    return fallback;
}
